/**
 * F35 (Wave 2F) — admin per-trainer billing page.
 *
 * Route: `/trainers/:trainerUserId/billing`
 * Permission: `payments:read` (super_admin + support + finance) — view.
 *             `trainer_billing:manage` (super_admin + finance) — override.
 *
 * Shows the trainer's current plan + effective commission rate, the plan & rate
 * history (the active plan + any queued switch — the frozen GET contract
 * exposes those two rows), and the trainer's invoices inline (reusing the F23
 * commission-statements queue filtered to this trainer). The "Override billing"
 * CTA opens `TrainerBillingOverrideDialog`; it is hidden for actors without
 * `trainer_billing:manage` (support sees a read-only page).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Chip,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type {
  AdminTrainerBillingOverrideInput,
  CommissionStatementStatus,
  TrainerBillingPlan,
} from '@bravemove/types';
import {
  adminTrainerBillingApi,
  type AdminTrainerBilling,
  type AdminTrainerInvoiceRow,
} from '../../../api/trainerBilling';
import { translateApiError } from '../../../auth/errors';
import { useAuth } from '../../../auth/useAuth';
import { PageHeader } from '../../../components/PageHeader';
import { TrainerBillingOverrideDialog } from '../../../components/commission/TrainerBillingOverrideDialog';

const STATUS_CHIP_MAP: Record<CommissionStatementStatus, StatusChipStatus> = {
  draft: 'pending',
  issued: 'in_review',
  settled: 'approved',
  void: 'rejected',
};

function formatRate(rateBp: number): string {
  return `${(rateBp / 100).toFixed(2)}%`;
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatPeriod(periodStart: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone: 'Asia/Beirut',
    }).format(new Date(periodStart));
  } catch {
    return periodStart;
  }
}

/** History rows shown in the plan/rate-history table (active + queued). */
interface HistoryEntry {
  readonly key: string;
  readonly plan: TrainerBillingPlan;
  readonly badge: 'active' | 'queued';
}

export function AdminTrainerBillingPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const params = useParams<{ trainerUserId: string }>();
  const trainerUserId = params.trainerUserId ?? '';

  const canManage = auth.adminUser?.permissions?.includes('trainer_billing:manage') ?? false;

  const [billing, setBilling] = useState<AdminTrainerBilling | null>(null);
  const [invoices, setInvoices] = useState<readonly AdminTrainerInvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, inv] = await Promise.all([
        adminTrainerBillingApi.getBilling(trainerUserId),
        adminTrainerBillingApi.listInvoices(trainerUserId, { perPage: 50 }),
      ]);
      setBilling(b);
      setInvoices(inv.data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [trainerUserId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchAll();
  }, [fetchAll]);

  const history = useMemo<readonly HistoryEntry[]>(() => {
    if (!billing) return [];
    const rows: HistoryEntry[] = [];
    if (billing.queuedSwitch) {
      rows.push({ key: 'queued', plan: billing.queuedSwitch, badge: 'queued' });
    }
    rows.push({ key: 'active', plan: billing.active, badge: 'active' });
    return rows;
  }, [billing]);

  const historyColumns = useMemo<readonly TableColumn<HistoryEntry>[]>(
    () => [
      {
        key: 'badge',
        // CST-010 — dedicated key (current.heading renders "Your current plan").
        header: t('trainer-billing:admin.history_col_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <Chip
            selected={row.badge === 'active'}
            data-testid={`trainer-billing-history-${row.key}`}
          >
            {row.badge === 'active'
              ? t('trainer-billing:comparison.current_label', { defaultValue: 'Current plan' })
              : // CST-010 — dedicated key (queued.banner interpolates
                // "Switching to {plan} on {date}." → "Switching to  on .").
                t('trainer-billing:admin.history_queued_chip', {
                  defaultValue: 'Queued switch',
                })}
          </Chip>
        ),
      },
      {
        key: 'mode',
        // CST-010 — dedicated key (invoices.col_source renders "Type").
        header: t('trainer-billing:admin.history_col_plan', { defaultValue: 'Plan' }),
        cell: (row) => (
          <span className="text-[13px] text-ink font-semibold">
            {t(`trainer-billing:mode.${row.plan.mode}`, {
              defaultValue: row.plan.mode === 'commission' ? 'Commission' : 'Flat monthly',
            })}
          </span>
        ),
      },
      {
        key: 'effectiveAt',
        // CST-010 — dedicated key (invoices.col_issued renders "Issued").
        header: t('trainer-billing:admin.history_col_effective', { defaultValue: 'Effective' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.plan.effectiveAt, locale)}</span>
        ),
      },
      {
        key: 'trial',
        header: t('trainer-billing:admin.override_trial', { defaultValue: 'Trial ends at' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.plan.trialEndsAt, locale)}</span>
        ),
      },
      {
        key: 'reason',
        // CST-010 — dedicated key (admin.override_reason renders "Reason (required)").
        header: t('trainer-billing:admin.history_col_reason', { defaultValue: 'Reason' }),
        cell: (row) => <span className="text-[12px] text-ink-3">{row.plan.reason ?? '—'}</span>,
      },
    ],
    [t, locale],
  );

  const invoiceColumns = useMemo<readonly TableColumn<AdminTrainerInvoiceRow>[]>(
    () => [
      {
        key: 'period',
        header: t('trainer-billing:invoices.col_period', { defaultValue: 'Period' }),
        cell: (row) => (
          <span className="text-[13px] text-ink">{formatPeriod(row.periodStart, locale)}</span>
        ),
      },
      {
        key: 'source',
        header: t('trainer-billing:invoices.col_source', { defaultValue: 'Type' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {t(`commission:source.${row.source}`, {
              defaultValue:
                row.source === 'trainer_flat' ? 'Trainer flat fee' : 'Trainer commission',
            })}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('trainer-billing:invoices.col_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP_MAP[row.status]}
            label={t(`commission:status.${row.status}`, { defaultValue: row.status })}
          />
        ),
      },
      {
        key: 'amount',
        header: t('trainer-billing:invoices.col_amount', { defaultValue: 'Amount' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay
            amount={
              row.source === 'trainer_flat'
                ? (row.flatFeeAmount ?? row.commissionAmountSum)
                : row.commissionAmountSum
            }
            currency="USD"
            locale={locale}
          />
        ),
      },
      {
        key: 'issuedAt',
        header: t('trainer-billing:invoices.col_issued', { defaultValue: 'Issued' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.issuedAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  const onConfirmOverride = async (input: AdminTrainerBillingOverrideInput) => {
    await adminTrainerBillingApi.applyOverride(trainerUserId, input);
    toast.push({
      tone: 'success',
      title: t('trainer-billing:admin.override_applied_toast', {
        defaultValue: 'Billing override applied.',
      }),
    });
    void fetchAll();
  };

  return (
    <div className="space-y-6" data-testid="trainer-billing-page">
      <PageHeader
        title={t('trainer-billing:admin.current_heading', { defaultValue: 'Current billing' })}
        // CST-010 — dedicated admin key (the shared `subtitle` carries
        // trainer-facing copy: "Choose how the platform bills you…").
        subtitle={t('trainer-billing:admin.subtitle', {
          defaultValue: 'Review and override how the platform bills this trainer.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="trainer-billing-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !billing ? (
        <EmptyState
          title={t('trainer-billing:errors.TRAINER_BILLING_PLAN_NOT_FOUND', {
            defaultValue: 'No billing plan found for this trainer.',
          })}
        />
      ) : (
        <>
          {/* Current plan + effective rate summary */}
          <section
            className="rounded-xl border border-hairline-2 bg-surface p-5 space-y-3"
            data-testid="trainer-billing-current"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-[15px] font-semibold text-ink">
                  {t('trainer-billing:current.heading', { defaultValue: 'Your current plan' })}
                </h2>
                <div className="flex items-center gap-2">
                  <Chip selected data-testid="trainer-billing-current-mode">
                    {t(`trainer-billing:mode.${billing.active.mode}`, {
                      defaultValue:
                        billing.active.mode === 'commission' ? 'Commission' : 'Flat monthly',
                    })}
                  </Chip>
                  {billing.active.mode === 'commission' ? (
                    <span
                      className="font-mono text-[13px] text-ink"
                      data-testid="trainer-billing-current-rate"
                    >
                      {formatRate(billing.effectiveRateBasisPoints)}
                    </span>
                  ) : (
                    <span
                      className="text-[13px] text-ink"
                      data-testid="trainer-billing-current-fee"
                    >
                      <CurrencyDisplay
                        amount={billing.active.flatFeeAmount ?? 0}
                        currency={billing.active.flatFeeCurrency}
                        locale={locale}
                      />
                    </span>
                  )}
                  {billing.rateSource === 'admin_override' ? (
                    <Chip data-testid="trainer-billing-rate-override-badge">
                      {t('trainer-billing:current.rate_override_note', {
                        defaultValue: 'A custom rate is applied to your account.',
                      })}
                    </Chip>
                  ) : null}
                </div>
              </div>
              {canManage ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOverrideOpen(true)}
                  data-testid="trainer-billing-override-cta"
                >
                  {t('trainer-billing:admin.override_title', {
                    defaultValue: 'Override billing for this trainer',
                  })}
                </Button>
              ) : (
                <Chip data-testid="trainer-billing-readonly">
                  {/* CST-010 — dedicated key (invoices.view renders "View"). */}
                  {t('trainer-billing:admin.readonly_chip', { defaultValue: 'Read-only' })}
                </Chip>
              )}
            </div>
            {billing.active.trialEndsAt ? (
              <Banner tone="info">
                {t('trainer-billing:trial.active_banner', {
                  date: formatDate(billing.active.trialEndsAt, locale),
                  defaultValue: 'On a billing trial.',
                })}
              </Banner>
            ) : null}
          </section>

          {/* Plan & rate history (active + queued) */}
          <section className="space-y-3" data-testid="trainer-billing-history">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('trainer-billing:admin.history_heading', { defaultValue: 'Plan & rate history' })}
            </h2>
            <Table
              data={history as HistoryEntry[]}
              columns={historyColumns as TableColumn<HistoryEntry>[]}
              rowKey={(row) => row.key}
            />
          </section>

          {/* Inline trainer invoices */}
          <section className="space-y-3" data-testid="trainer-billing-invoices">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('trainer-billing:invoices.title', { defaultValue: 'Invoices' })}
            </h2>
            {invoices.length === 0 ? (
              <EmptyState
                title={t('trainer-billing:invoices.empty', { defaultValue: 'No invoices yet.' })}
              />
            ) : (
              <Table
                data={invoices as AdminTrainerInvoiceRow[]}
                columns={invoiceColumns as TableColumn<AdminTrainerInvoiceRow>[]}
                rowKey={(row) => row.id}
                onRowPress={(row) => navigate(`/commission-statements/${row.id}`)}
              />
            )}
          </section>
        </>
      )}

      {billing && overrideOpen ? (
        <TrainerBillingOverrideDialog
          open={overrideOpen}
          trainerUserId={trainerUserId}
          trainerLabel={trainerUserId}
          currentMode={billing.active.mode}
          currentRateBasisPoints={billing.effectiveRateBasisPoints}
          currentTrialEndsAt={billing.active.trialEndsAt}
          onConfirm={onConfirmOverride}
          onClose={() => setOverrideOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default AdminTrainerBillingPage;
